import classNames from 'classnames';
import camelCase from 'lodash-es/camelCase';
import isNumber from 'lodash-es/isNumber';
import { useEffect, useRef, useState, ReactNode, CSSProperties, Ref, startTransition } from 'react';

import useElementSize from '../../hooks/useElementSize';

import Picture from './Picture';

import styles from '../../styles/partials/image.module.css';

export interface ImageProps {
    media?: Image | null;
    width?: number | string | null;
    height?: number | string | null;
    ratio?: number | null;
    size?: string | null;
    position?: string | null;
    loading?: 'lazy' | null;
    alt?: string | null;
    children?: ReactNode;
    withoutSize?: boolean;
    withPictureSize?: boolean;
    responsive?: boolean;
    autoSize?: boolean;
    fetchPriority?: 'high' | 'low' | 'auto' | null;
    pictureStyle?: CSSProperties | null;
    withPlaceholder?: boolean;
    withoutLoadingTransition?: boolean;
    className?: string | null;
    pictureClassName?: string | null;
}

function Image({
    media = null,
    width = null,
    height = null,
    ratio = null,
    size = null,
    position = null,
    loading: loadingMode = null,
    alt = null,
    children = null,
    withoutSize = false,
    withPictureSize = false,
    responsive = false,
    autoSize = false,
    fetchPriority = null,
    pictureStyle = null,
    withPlaceholder = false,
    withoutLoadingTransition = false,
    className = null,
    pictureClassName = null,
}: ImageProps) {
    const {
        ref: refResize,
        width: containerWidth = 0,
        height: containerHeight = 0,
    } = useElementSize({
        disabled: !autoSize,
    });
    const finalWidth = autoSize ? width || containerWidth : width;
    const finalHeight = autoSize ? height || containerHeight : height;
    let style = null;
    if (responsive && isNumber(width) && isNumber(height)) {
        style = {
            width: '100%',
            height: 0,
            paddingBottom: `${(ratio || height / width) * 100}%`,
        };
    } else if (!withoutSize) {
        style = {
            width: finalWidth,
            height: finalHeight,
        };
    }
    const { paddingBottom = null } = style || {};

    const pictureRef = useRef<HTMLImageElement | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const onPictureLoaded = () => {
        startTransition(() => {
            setLoading(false);
            setLoaded(true);
        });
    };

    useEffect(() => {
        const loaded =
            media !== null &&
            pictureRef.current !== null &&
            (pictureRef.current.naturalWidth > 0 || pictureRef.current.complete);
        startTransition(() => {
            setLoading(!loaded);
            setLoaded(loaded);
        });
    }, [media]);

    const { preview = null } = media || {};

    return (
        <div
            className={classNames([
                styles.container,
                {
                    'contain-size': paddingBottom !== null,
                    [styles.placeholder]: withPlaceholder,
                    [styles.hasPreview]: preview !== null,
                    [styles.loading]: loading && !withoutLoadingTransition,
                    [styles.loaded]: loaded,
                    [styles.withPictureSize]: withPictureSize,
                },
                styles[camelCase(position)],
                className,
            ])}
            ref={refResize as Ref<HTMLDivElement>}
            style={style}
        >
            {media !== null ? (
                <Picture
                    media={media}
                    className={classNames([styles.picture, pictureClassName])}
                    ref={pictureRef}
                    width={finalWidth}
                    height={finalHeight}
                    loading={loadingMode}
                    size={size}
                    alt={alt}
                    fetchPriority={fetchPriority}
                    style={
                        preview !== null
                            ? {
                                  backgroundImage: `url("${preview}")`,
                                  ...pictureStyle,
                              }
                            : pictureStyle
                    }
                    onLoad={onPictureLoaded}
                />
            ) : null}
            {preview !== null ? (
                <div
                    className={styles.preview}
                    style={{
                        backgroundImage: `url("${preview}")`,
                    }}
                />
            ) : null}
            {children}
        </div>
    );
}

export default Image;
